import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { SpecsValidationIssue } from "../../service/SpecsRevisionService";
import { palette } from "../../styles/theme";
import {
  filterDisplayedIssues,
  formatSpecsIssueLocation,
  getSpecsIssueSeverity,
} from "./specsValidationIssues";

export type SpecsEditorValidationResult = {
  valid: boolean;
  issues: SpecsValidationIssue[];
  imageValidationSkipped?: boolean;
};

export type SpecsValidationDialogApi = {
  validationResult: SpecsEditorValidationResult | null;
  validationOpen: boolean;
  showValidationResult: (result: SpecsEditorValidationResult) => void;
  openValidationDialog: () => void;
  closeValidationDialog: () => void;
};

export function useSpecsValidationDialogApi(): SpecsValidationDialogApi {
  const [validationResult, setValidationResult] =
    React.useState<SpecsEditorValidationResult | null>(null);
  const [validationOpen, setValidationOpen] = React.useState(false);

  const showValidationResult = React.useCallback(
    (result: SpecsEditorValidationResult) => {
      setValidationResult(result);
      const displayed = filterDisplayedIssues(result.issues ?? []);
      setValidationOpen(!result.valid || displayed.length > 0);
    },
    []
  );

  const closeValidationDialog = React.useCallback(() => {
    setValidationOpen(false);
  }, []);

  const openValidationDialog = React.useCallback(() => {
    setValidationOpen(true);
  }, []);

  return React.useMemo(
    () => ({
      validationResult,
      validationOpen,
      showValidationResult,
      openValidationDialog,
      closeValidationDialog,
    }),
    [
      closeValidationDialog,
      openValidationDialog,
      showValidationResult,
      validationOpen,
      validationResult,
    ]
  );
}

function IssueSeverityChip({ severity }: { severity?: "error" | "warning" }) {
  if (severity === "warning") {
    return (
      <Chip
        label="warning"
        size="small"
        sx={{
          bgcolor: "#fff3e0",
          color: "#e65100",
          fontWeight: 600,
          fontSize: "11px",
          height: 20,
        }}
      />
    );
  }
  return (
    <Chip
      label="error"
      size="small"
      sx={{
        bgcolor: "#fce4ec",
        color: "#c62828",
        fontWeight: 600,
        fontSize: "11px",
        height: 20,
      }}
    />
  );
}

function IssueCard({ issue }: { issue: SpecsValidationIssue }) {
  const severity = getSpecsIssueSeverity(issue);
  const isWarning = severity === "warning";
  const location = formatSpecsIssueLocation(issue);

  return (
    <Box
      sx={{
        border: `1px solid ${isWarning ? "#ffcc80" : "#ef9a9a"}`,
        borderLeft: `3px solid ${isWarning ? "#ff9800" : "#ef5350"}`,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        bgcolor: "white",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <IssueSeverityChip severity={severity} />
        {issue.validator ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {issue.validator}
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {issue.message}
      </Typography>

      {location ? (
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: palette.grey.lightBackground,
            fontFamily: "monospace",
            fontSize: "12px",
            lineHeight: 1.6,
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "11px", color: "text.secondary" }}
          >
            Field:{" "}
          </Typography>
          {location}
        </Box>
      ) : null}

      {issue.details ? (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: "text.secondary",
            fontSize: "12px",
          }}
        >
          {issue.details}
        </Typography>
      ) : null}

      {issue.stack ? (
        <Box
          component="pre"
          sx={{
            mt: 1,
            m: 0,
            p: 1,
            borderRadius: 1,
            bgcolor: palette.grey.lightBackground,
            overflowX: "auto",
            fontSize: "11px",
            lineHeight: 1.5,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {issue.stack}
        </Box>
      ) : null}
    </Box>
  );
}

type SpecsValidationDialogProps = {
  open: boolean;
  validationResult: SpecsEditorValidationResult | null;
  onClose: () => void;
};

export default function SpecsValidationDialog({
  open,
  validationResult,
  onClose,
}: SpecsValidationDialogProps) {
  const issues = filterDisplayedIssues(validationResult?.issues ?? []);
  const errorCount = issues.filter(
    (issue) => getSpecsIssueSeverity(issue) === "error"
  ).length;
  const warningCount = issues.filter(
    (issue) => getSpecsIssueSeverity(issue) === "warning"
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{ "& .MuiDialog-paper": { maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Specs Validation</DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {validationResult?.valid ? (
          <Alert severity="success">No validation issues found.</Alert>
        ) : (
          <Stack spacing={1}>
            <Alert severity={errorCount > 0 ? "error" : "warning"}>
              {errorCount > 0 ? (
                <>
                  {errorCount} error{errorCount === 1 ? "" : "s"}
                  {warningCount > 0
                    ? ` and ${warningCount} warning${warningCount === 1 ? "" : "s"}`
                    : ""}
                </>
              ) : (
                <>
                  {warningCount} warning{warningCount === 1 ? "" : "s"}
                </>
              )}{" "}
              found.
            </Alert>
            {issues.map((issue, index) => (
              <IssueCard
                key={`${formatSpecsIssueLocation(issue)}-${issue.message}-${index}`}
                issue={issue}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
